import { GridContextProvider, GridDropZone, GridItem } from "react-grid-dnd";
import {
  Box,
  IconButton,
  TablePagination,
  Paper,
  Button,
  Alert,
  AlertColor,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useContext, useEffect, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import axios from "axios";

import { AuthContext } from "../../contexts/AuthContext";
import { CONFIG } from "../../services/config";
import ReorderModal from "../reorderModal";
import { FormInputText } from "../form/FormInputText";
import {
  Data,
  PageStateType,
  ReorderTableProps,
} from "../../interfaces/ReorderTable";
import QuickAlert from "../form/quickAlert";

export default function ReorderTable({
  endPoint,
  host,
  messageError,
  rowHeight = 80,
  reorderEndPoint,
  reorderPayload,
  onReorderedItem,
  params1,
  labelParams1,
  cleanButtonStyle,
  cleanButtonType,
  cleanButtonText,
  columnsData,
  onRowClick,
  refreshTable = 0,
  onPageStateChange,
  emptyTableMessage,
  tableWidthMobile,
  gridColumns,
}: ReorderTableProps) {
  const { t } = useTranslation();
  const [searchParams1, setSearchParams1] = useState("");
  const [errorFetching, setErrorFetching] = useState(false);
  const [openReorderModal, setOpenReorderModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [pageState, setPageState] = useState<PageStateType>({
    isLoading: false,
    data: [],
    total: 0,
    page: 0,
    pageSize: 25,
  });
  const [reorderedItem, setReorderedItem] = useState<{
    item: Data;
    toOrder: number;
  } | null>();

  const [quickAlertProps, setQuickAlertProps] = useState<{
    message: string;
    titleQuickAlert: string;
    severity: AlertColor;
  }>();
  const rowsOnPage = pageState.data.length;
  const dropZoneHeight = rowsOnPage * rowHeight;

  const {
    tokenHeaders,
    country: { countryCode },
    user,
  } = useContext(AuthContext);

  const onChange = (
    _sourceId: string,
    sourceIndex: number,
    targetIndex: number,
  ) => {
    if (sourceIndex === undefined || targetIndex === undefined) return;
    if (targetIndex > pageState.data.length - 1) return;
    if (searchParams1) return;

    const movedItem = pageState.data[sourceIndex];
    const destination = pageState.data[targetIndex];
    setReorderedItem({
      item: movedItem,
      toOrder: destination.displayOrder,
    });
    onReorderedItem({
      item: movedItem,
      toOrder: destination.displayOrder,
    });
    setOpenReorderModal(true);
  };

  const searchWithQueryParams = async () => {
    tokenHeaders.headers.countryCode = countryCode;
    tokenHeaders.headers["Content-Type"] = "application/json";
    axios
      .get(
        `${host || CONFIG.API_URL}${endPoint}page=${pageState.page}&size=${
          pageState.pageSize
        }${params1}${searchParams1}`,
        {
          data: null,
          headers: tokenHeaders.headers,
        },
      )
      .then((response) => {
        setErrorFetching(false);

        setPageState((old) => ({
          ...old,
          isLoading: false,
          data: response.data.content ? response.data.content : response.data,
          total: response.data.totalElements,
        }));
      })
      .catch((error: any) => {
        setErrorFetching(true);

        if (error.response.data.message && messageError) {
          return messageError(error.response.data.message);
        }
      });
  };

  const searchFullData = async () => {
    tokenHeaders.headers["Content-Type"] = "application/json";
    axios
      .get(
        `${host || CONFIG.API_URL}${endPoint}page=${pageState.page}&size=${
          pageState.pageSize
        }`,
        tokenHeaders,
      )
      .then((response) => {
        setErrorFetching(false);

        setPageState((old) => ({
          ...old,
          isLoading: false,
          data: response.data.content ? response.data.content : response.data,
          total: response.data.totalElements,
        }));
      })
      .catch((error: any) => {
        setErrorFetching(true);

        if (error.response.data.message) {
          return messageError(error.response.data.message);
        }
      });
  };

  const updateData = async () => {
    setPageState((old) => ({ ...old, isLoading: true }));
    tokenHeaders.headers.email = user.email;
    tokenHeaders.headers.countryCode = countryCode;
    tokenHeaders.headers["Content-Type"] = "application/json";
    axios
      .put(
        `${host || host || CONFIG.API_URL}${reorderEndPoint}${
          reorderedItem?.item.id
        }`,
        reorderPayload,
        tokenHeaders,
      )
      .then(() => {
        setErrorFetching(false);
        searchFullData();
      })
      .catch((error: any) => {
        setErrorFetching(true);

        if (error.response.data.message && messageError) {
          return messageError(error.response.data.message);
        }
      });
  };

  const clearInputs = () => {
    const input1 = document.querySelector("#label1") as HTMLInputElement;

    input1 ? (input1.value = "") : true;

    setSearchParams1("");

    searchFullData();
  };

  const searchData = async () => {
    setPageState((old) => ({ ...old, isLoading: true }));

    !searchParams1 ? (params1 = "") : true;

    searchWithQueryParams();
  };

  const getCellTitle = (col: GridColDef, item: any) => {
    const raw = item[col.field];
    return typeof raw === "string" ? raw : "";
  };

  useEffect(() => {
    searchFullData();
  }, [refreshTable, pageState.page, pageState.pageSize]);

  useEffect(() => {
    if (onPageStateChange) onPageStateChange(pageState);
  }, [pageState.data]);

  return (
    <>
      <Box>
        {params1 && labelParams1 && (
          <>
            <FormInputText
              id="label1"
              type="text"
              label={labelParams1}
              onChange={(event) => setSearchParams1(event.target.value)}
              onKeyDown={(event: any) => {
                if (event.key === "Enter") {
                  searchData();
                }
              }}
            />
            <Box
              sx={{
                display: "flex",
                gap: 2,
                marginBottom: 2,
              }}
            >
              <Button
                variant="contained"
                onClick={() => {
                  searchData();
                }}
              >
                {t("button.Search")}
              </Button>
              <Button
                sx={cleanButtonStyle ? cleanButtonStyle : null}
                variant={cleanButtonType ? cleanButtonType : "contained"}
                onClick={() => {
                  clearInputs();
                }}
              >
                {cleanButtonText || t("button.Reset")}
              </Button>
            </Box>
          </>
        )}

        {errorFetching && (
          <Box sx={{ marginBottom: 2 }}>
            <span style={{ color: "var(--red-100)" }}>
              {searchParams1 ? (
                `${t("alert.notResult")}: ${searchParams1}`
              ) : (
                <>
                  <Alert severity="error">{t("alert.errorFetch")}</Alert>
                  <Alert severity="warning" sx={{ marginY: "1rem" }}>
                    {t("alert.tryAgain")}
                  </Alert>
                </>
              )}
            </span>
          </Box>
        )}
      </Box>
      <Box sx={{ overflowX: "auto" }}>
        <Paper
          variant="outlined"
          sx={{
            fontSize: 14,
            fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
            border: "1px solid rgb(224, 224, 224)",
            overflowX: "auto",
            width: window.innerWidth > 900 ? "auto" : tableWidthMobile,
          }}
        >
          {/* Cabeçalho */}
          <Box
            minHeight="4rem"
            sx={{
              backgroundColor: "var(--gray-100)",
              display: "grid",
              gridTemplateColumns: gridColumns,
            }}
            alignItems="center"
          >
            {columnsData.map((col) => (
              <Box
                key={col.field}
                sx={{
                  pl: 2,
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  fontWeight: 600,
                }}
              >
                {col.headerName}
              </Box>
            ))}
          </Box>
          {pageState.data.length ? (
            <>
              {/* Linhas */}
              <GridContextProvider onChange={onChange}>
                <GridDropZone
                  id="items"
                  boxesPerRow={1}
                  rowHeight={rowHeight}
                  style={{ width: "100%", height: dropZoneHeight }}
                >
                  {pageState.data.map((item) => (
                    <GridItem key={item.id}>
                      <Box
                        alignItems="center"
                        borderBottom="1px solid rgb(224, 224, 224)"
                        height={80}
                        onPointerUp={() => {
                          if (!isDragging && onRowClick) {
                            onRowClick(item);
                          }
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                          "&:active": {
                            backgroundColor: "rgba(0, 175, 242, 0.08)",
                          },
                          display: "grid",
                          gridTemplateColumns: gridColumns,
                        }}
                      >
                        {columnsData.map((col) => (
                          <Box
                            key={col.field}
                            sx={{
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              height: rowHeight,
                              pl: 2,
                            }}
                            title={getCellTitle(col, item)}
                            onPointerDown={() => setIsDragging(false)}
                            onPointerMove={() => setIsDragging(true)}
                            onTouchMove={(event) =>
                              col.field !== "__drag" && event.stopPropagation()
                            }
                            onMouseMove={(event) =>
                              col.field !== "__drag" && event.stopPropagation()
                            }
                          >
                            <Box
                              style={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {col.field === "__drag" ? (
                                <IconButton
                                  size="small"
                                  sx={{ cursor: "grab" }}
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <DragIndicatorIcon />
                                </IconButton>
                              ) : col.renderCell ? (
                                col.renderCell({
                                  row: item,
                                  value: item[col.field],
                                  field: col.field,
                                  id: item.id,
                                  formattedValue: item[col.field],
                                  api: {} as any,
                                  colDef: col as any,
                                  getValue: () => item[col.field],
                                  isEditable: false,
                                  hasFocus: false,
                                  tabIndex: -1,
                                  rowNode: {} as any,
                                  cellMode: "view",
                                })
                              ) : (
                                item[col.field]
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </GridItem>
                  ))}
                </GridDropZone>
              </GridContextProvider>
            </>
          ) : (
            <Box textAlign="center" padding={6}>
              {emptyTableMessage || t("banners.table.emptyTable")}
            </Box>
          )}

          <TablePagination
            component="table"
            count={pageState.total}
            page={pageState.page}
            onPageChange={(_, newPage) =>
              setPageState((old) => ({ ...old, page: newPage }))
            }
            rowsPerPage={pageState.pageSize}
            onRowsPerPageChange={(event) => {
              const newPageSize = parseInt(event.target.value, 10);
              setPageState((old) => ({
                ...old,
                pageSize: newPageSize,
                page: 0,
              }));
            }}
            rowsPerPageOptions={[25, 50, 75, 100]}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              borderTop: "1px solid rgb(224, 224, 224)",
            }}
          />
        </Paper>
      </Box>

      {openReorderModal && (
        <ReorderModal
          onClose={() => {
            if (reorderPayload) onReorderedItem(null);
            setOpenReorderModal(false);
          }}
          onConfirm={() => {
            updateData().finally(() => {
              if (reorderPayload) onReorderedItem(null);
              setOpenReorderModal(false);
              setPageState((old) => ({ ...old, isLoading: false }));
              setQuickAlertProps({
                message: t("banners.quickAlert.modalCreateBannerSuccess"),
                titleQuickAlert: t("quickAlert.sucess"),
                severity: "success",
              });
            });
          }}
        />
      )}
      <QuickAlert
        visibility={!!quickAlertProps?.message}
        severity={quickAlertProps?.severity}
        title={quickAlertProps?.titleQuickAlert}
        description={quickAlertProps?.message}
      />
    </>
  );
}
